import React, { useState, useMemo, useEffect } from 'react';
import {
  Drawer,
  Box,
  Typography,
  IconButton,
  Paper,
  Button,
  CircularProgress,
  TextField
} from '@mui/material';
import { X, Search } from 'lucide-react';
import { useNavbarVisibility } from '../../../context/NavbarVisibilityContext';

const PinRetreatDrawer = ({
  open,
  onClose,
  matchingRetreats = [],
  loading = false
}) => {
  const [searchQuery, setSearchQuery] = useState('');
  const { setHideNavbar } = useNavbarVisibility();

  useEffect(() => {
    setHideNavbar(open);
    return () => {
      setHideNavbar(false);
    };
  }, [open, setHideNavbar]);

  // Filter retreats by inside-drawer search query
  const filteredRetreats = useMemo(() => {
    if (!Array.isArray(matchingRetreats)) return [];
    if (!searchQuery.trim()) return matchingRetreats;
    const q = searchQuery.toLowerCase().trim();
    return matchingRetreats.filter((r) => {
      const title = (r.title || r.name || '').toLowerCase();
      const city = (r.location?.city || '').toLowerCase();
      const state = (r.location?.state || '').toLowerCase();
      return (
        title.includes(q) ||
        city.includes(q) ||
        state.includes(q)
      );
    });
  }, [matchingRetreats, searchQuery]);

  return (
    <Drawer
      anchor="right"
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          width: { xs: '100%', sm: 460 },
          maxWidth: '100vw',
          backgroundColor: '#FFFFFF',
          borderLeft: '1px solid #E2E8F0',
          boxShadow: 'none',
          display: 'flex',
          flexDirection: 'column'
        }
      }}
    >
      {/* Header */}
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderBottom: '1px solid #E2E8F0',
          backgroundColor: '#FFFFFF',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start'
        }}
      >
        <Box sx={{ pr: 1 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
            <Typography sx={{ fontWeight: 600, fontSize: 15, color: '#1E293B' }}>
              Matching Retreats
            </Typography>
          </Box>
          <Typography sx={{ color: '#64748B', fontSize: 11.5, lineHeight: 1.45 }}>
            {matchingRetreats.length} retreats
          </Typography>
        </Box>
        <IconButton
          size="small"
          onClick={onClose}
          sx={{
            color: '#64748B',
            border: '1px solid #E2E8F0',
            borderRadius: '6px',
            p: 0.75,
            '&:hover': { color: '#1E293B', backgroundColor: '#F8FAFC' }
          }}
        >
          <X size={16} />
        </IconButton>
      </Box>

      {/* Search Input inside drawer */}
      <Box sx={{ px: 2.5, py: 1.5, borderBottom: '1px solid #EEF2F6', backgroundColor: '#F8FAFC' }}>
        <TextField
          size="small"
          fullWidth
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          placeholder="Filter by retreat name or city..."
          InputProps={{
            startAdornment: (
              <Box sx={{ mr: 1, display: 'flex', alignItems: 'center', color: '#94A3B8' }}>
                <Search size={15} />
              </Box>
            ),
            endAdornment: searchQuery ? (
              <IconButton size="small" onClick={() => setSearchQuery('')} sx={{ color: '#94A3B8', p: 0.5 }}>
                <X size={13} />
              </IconButton>
            ) : null
          }}
          sx={{
            '& .MuiOutlinedInput-root': {
              backgroundColor: '#FFFFFF',
              borderRadius: '6px',
              fontSize: 12.5,
              height: 36,
              '& fieldset': { borderColor: '#E2E8F0' },
              '&:hover fieldset': { borderColor: '#CBD5E1' },
              '&.Mui-focused fieldset': { borderColor: '#6E8964', borderWidth: '1px' }
            }
          }}
        />
      </Box>

      {/* Drawer Body */}
      <Box sx={{ p: 2.5, overflowY: 'auto', flex: 1, backgroundColor: '#FFFFFF' }}>
        {loading ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 8, gap: 1.5 }}>
            <CircularProgress size={26} sx={{ color: '#6E8964' }} />
            <Typography sx={{ color: '#64748B', fontSize: 12 }}>
              Loading matching retreats...
            </Typography>
          </Box>
        ) : filteredRetreats.length === 0 ? (
          <Box
            sx={{
              textAlign: 'center',
              py: 7,
              px: 3,
              border: '1px dashed #E2E8F0',
              borderRadius: '8px',
              backgroundColor: '#F8FAFC'
            }}
          >
            <Typography sx={{ fontWeight: 600, fontSize: 13.5, color: '#1E293B', mb: 0.5 }}>
              No retreats found
            </Typography>
            <Typography sx={{ color: '#64748B', fontSize: 11.5, lineHeight: 1.5 }}>
              {searchQuery
                ? `No retreats match "${searchQuery}". Clear your search query.`
                : 'No live retreats match the currently active filters. Loosen your filter selections in the Retreat Filters card.'}
            </Typography>
            {searchQuery && (
              <Button
                variant="outlined"
                size="small"
                onClick={() => setSearchQuery('')}
                sx={{
                  mt: 1.5,
                  borderColor: '#E2E8F0',
                  color: '#803927',
                  fontSize: 11.5,
                  textTransform: 'none',
                  borderRadius: '6px'
                }}
              >
                Clear search
              </Button>
            )}
          </Box>
        ) : (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            {filteredRetreats.map((r) => {
              const rId = String(r.id);
              const firstPhoto = r.galleries?.find(g => g.mediaType === 'photo');
              const firstImage = firstPhoto?.fileUrl || null;

              return (
                <Paper
                  key={rId}
                  elevation={0}
                  sx={{
                    border: '1px solid #E2E8F0',
                    backgroundColor: '#FFFFFF',
                    borderRadius: '8px',
                    p: 1.5,
                    display: 'flex',
                    gap: 1.5,
                    boxShadow: 'none',
                    transition: 'none'
                  }}
                >
                  {/* Thumbnail */}
                  <Box
                    sx={{
                      width: 68,
                      height: 68,
                      borderRadius: '6px',
                      flexShrink: 0,
                      overflow: 'hidden',
                      backgroundColor: '#F1F5F9',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}
                  >
                    {firstImage ? (
                      <Box
                        component="img"
                        src={firstImage}
                        alt={r.title || 'Retreat'}
                        sx={{ width: '100%', height: '100%', objectFit: 'cover' }}
                      />
                    ) : (
                      <Box
                        sx={{
                          width: '100%',
                          height: '100%',
                          background: 'linear-gradient(135deg, #6E8964, #EFE4CB)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          color: '#FFFFFF',
                          fontSize: 18,
                          fontWeight: 600
                        }}
                      >
                        {(r.title || 'R')[0]}
                      </Box>
                    )}
                  </Box>

                  {/* Info */}
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography
                      title={r.title}
                      sx={{
                        fontWeight: 600,
                        fontSize: 12.5,
                        color: '#1E293B',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                        mb: 0.25
                      }}
                    >
                      {r.title}
                    </Typography>

                    <Typography sx={{ color: '#64748B', fontSize: 11 }}>
                      {r.location?.city && r.location?.state
                        ? `${r.location.city}, ${r.location.state}`
                        : r.location?.city || r.location?.state || 'No location'}
                      · {r.schedules?.[0]?.durationDays || 'N/A'} days
                    </Typography>
                  </Box>
                </Paper>
              );
            })}
          </Box>
        )}
      </Box>
    </Drawer>
  );
};

export default React.memo(PinRetreatDrawer);
